import { Rectangle, ClipbookInfo } from 'ark-common';
import { ComposerMode, ClipbookState, PagePicture } from '../types';
import ComposerAction, {
  ActionType,
  isComposerAction,
} from '../actions/ComposerAction';
import {
  DrawingState, collectPictureIDsFromDrawingState, drawing,
} from '../../drawing/reducers/drawing';
import drawingTool, {
  DrawingToolState,
  initDrawingToolState,
} from '../../drawing/reducers/drawingTool';
import history, {
  initHistory,
  forEachSnapshot,
  recordForAction,
  HistoryState,
} from '../../reducers/history';
import autoSaving, {
  AutoSavingState, isAutoSavingRunning, didAutoSavingStart, didAutoSavingRestart,
} from '../../reducers/autoSaving';
import pictureStorage, {
  PictureStorageState,
  initPictureStorage,
  prunePictureItems,
  preparePictureItemsForDrawing,
} from '../../drawing/reducers/pictureStorage';
import AutoSavingAction from '../../actions/AutoSavingAction';
import { Action } from 'redux';
import DrawingToolAction from '../../drawing/actions/DrawingToolAction';
import HistoryAction from '../../actions/HistoryAction';
import { isDrawingAction, isEditingDrawingAction } from '../../drawing/actions/DrawingAction';
import { reducePictureStorageByDrawingAction } from '../../drawing/reducers/drawingPicture';
import PictureStorageAction from '../../drawing/actions/PictureStorageAction';
import {
  ComposerViewState,
  defaultViewState,
  resetView,
  zoomTo,
  setPan,
  minComposerZoomRatio,
} from './view';
import { addPagePicture, createIsPageImageDemanded, addNewPage, moveToPage } from './page';
import {
  applyClipbookModification,
  prepareClipbookSaving,
  isReadyToSaveClipbook,
  startClipbookSaving,
  JSONReadyClipbookData,
} from './clipbook';
import {
  ComposerSnapshotKind,
  takeDrawingSnapshot,
  dataReplacedBySnapshot,
  consumeSnapshot,
  takeClipbookSnapshot,
  ComposerSnapshot,
} from './snapshot';

export type ComposerHistoryState = HistoryState<ComposerSnapshot>;

export enum ComposerPictureStorage {
  Pages,
  Drawing,
}

export interface ClipboardState {
  readonly pictureID: string;
  readonly pictureWidth: number;
  readonly pictureHeight: number;
  readonly croppingRect: Rectangle;
}

export interface EntireComposerState {
  readonly mode: ComposerMode;
  readonly clipbook: ClipbookState | null;
  readonly clipboard: ClipboardState | null;
  readonly pagePictures: PictureStorageState<PagePicture>;
  readonly clientRect: Rectangle;
  readonly view: ComposerViewState;
  readonly drawings: { readonly [pageID: string]: DrawingState };
  readonly drawingTool: DrawingToolState;
  readonly drawingPictures: PictureStorageState<HTMLCanvasElement>;
  readonly history: ComposerHistoryState;
  readonly autoSaving: AutoSavingState;
  readonly existingClipbooks: ReadonlyArray<ClipbookInfo>;
  readonly savingClipbook: { id: string, body: JSONReadyClipbookData } | null;
}

export function initEntireComposerState(): EntireComposerState {
  return {
    mode: ComposerMode.LoadingIndex,
    clipbook: null,
    clipboard: null,
    pagePictures: initPictureStorage(),
    clientRect: { x: 0, y: 0, width: 0, height: 0 },
    view: defaultViewState,
    drawings: {},
    drawingTool: initDrawingToolState(),
    drawingPictures: initPictureStorage(),
    history: initHistory(),
    autoSaving: AutoSavingState.Idle,
    existingClipbooks: [],
    savingClipbook: null,
  };
}

export function createIsPagePictureUsed(
  clipbook: ClipbookState | null,
  clipboard: ClipboardState | null,
  history: ComposerHistoryState,
) {
  const ids: { [id: string]: true } = {};

  if (clipbook !== null) {
    for (const page of clipbook.pages) {
      ids[page.backgroundID] = true;
    }
  }

  if (clipboard !== null) {
    ids[clipboard.pictureID] = true;
  }

  forEachSnapshot(history, (item) => {
    if (item.kind === ComposerSnapshotKind.Clipbook) {
      for (const page of item.pages) {
        ids[page.backgroundID] = true;
      }
    }
  });

  return (id: string) => id in ids;
}

export function createIsDrawingImageDemanded(
  clipbook: ClipbookState | null,
  drawings: { readonly [pageID: string]: DrawingState },
) {
  const ids: { [id: string]: true } = {};

  if (clipbook !== null && clipbook.currentPage !== null) {
    const { uniqueID } = clipbook.pages[clipbook.currentPage];
    for (const id of collectPictureIDsFromDrawingState(drawings[uniqueID])) {
      ids[id] = true;
    }
  }

  return (id: string) => id in ids;
}

export function createIsDrawingPictureUsed(
  drawings: { readonly [pageID: string]: DrawingState },
  history: ComposerHistoryState,
) {
  const ids: { [id: string]: true } = {};

  for (const pageID in drawings) {
    for (const id of collectPictureIDsFromDrawingState(drawings[pageID])) {
      ids[id] = true;
    }
  }

  forEachSnapshot(history, (item) => {
    if (item.kind === ComposerSnapshotKind.Drawing) {
      for (const id of collectPictureIDsFromDrawingState(item.data)) {
        ids[id] = true;
      }
    }
  });

  return (id: string) => id in ids;
}

function reduceEntireComposerStateNew(
  state: EntireComposerState,
  action: ComposerAction,
): EntireComposerState {
  switch (action.type) {
    case ActionType.SET_CLIENT_RECT: {
      const intermediateState = { ...state, clientRect: action.clientRect };
      // TODO : Adjust the view instead of resetting.
      return resetView(intermediateState);
    }

    case ActionType.SET_COMPOSER_MODE:
      return { ...state, mode: action.mode };

    case ActionType.ZOOM_IN:
      if (state.clipbook === null) {
        throw new Error('Illegal state.');
      }

      return zoomTo(state, state.view.zoom + 0.1);

    case ActionType.ZOOM_OUT:
      if (state.clipbook === null) {
        throw new Error('Illegal state.');
      }

      return zoomTo(
        state,
        Math.max(state.view.zoom - 0.1, minComposerZoomRatio(state.clipbook, state.clientRect)),
      );

    case ActionType.RESET_ZOOM:
      return resetView(state);

    case ActionType.SET_PAN:
      return setPan(state, action.pan);

    case ActionType.RECEIVE_NEW_PAGE_FROM_CLIPBOARD: {
      const { pictureID, pictureBlob, pictureURL, pictureInfo } = action;
      let intermediateState: EntireComposerState = {
        ...state,
        mode: (state.clipbook === null) ? state.mode : ComposerMode.NewPage,
        clipboard: {
          pictureID,
          pictureWidth: pictureInfo.width,
          pictureHeight: pictureInfo.height,
          croppingRect: pictureInfo.clientRect,
        },
      };
      intermediateState = addPagePicture(intermediateState, pictureID, pictureBlob, pictureURL);
      return intermediateState;
    }

    case ActionType.SET_CROPPING_RECT:
      if (state.clipboard === null) {
        throw new Error('Illegal state.');
      }

      return { ...state, clipboard: { ...state.clipboard, croppingRect: action.rect } };

    case ActionType.DISCARD_CLIPBOARD: {
      const intermediateState = {
        ...state,
        mode: (state.clipbook !== null && state.clipbook.pages.length > 0) ?
          ComposerMode.Editing : state.mode,
        clipboard: null,
      };
      return intermediateState;
    }

    case ActionType.CREATE_NEW_CLIPBOOK: {
      let intermediateState: EntireComposerState = {
        ...state,
        mode: ComposerMode.NewPage,
        clipbook: {
          id: action.id,
          name: '新しいバインダー',
          pages: [],
          currentPage: null,
        },
      };
      intermediateState = resetView(intermediateState);
      intermediateState = applyClipbookModification(intermediateState);
      return intermediateState;
    }

    case ActionType.ADD_NEW_PAGE: {
      let intermediateState =
        addNewPage(state, action.uniqueID, action.backgroundID, action.width, action.height);
      intermediateState =
        applyClipbookModification(intermediateState);
      return intermediateState;
    }

    case ActionType.ADD_NEW_PAGE_WITH_PICTURE: {
      let intermediateState =
        addNewPage(state, action.uniqueID, action.backgroundID, action.width, action.height);
      intermediateState = addPagePicture(
        intermediateState,
        action.backgroundID,
        action.backgroundBlob,
        action.backgroundURL,
      );
      intermediateState = applyClipbookModification(intermediateState);
      return intermediateState;
    }

    case ActionType.MOVE_TO_PAGE: {
      let intermediateState = moveToPage(state, action.destination);

      if (intermediateState !== state) {
        intermediateState = applyClipbookModification(intermediateState);
      }

      return intermediateState;
    }

    case ActionType.LOAD_CLIPBOOK_INDEX:
      return {
        ...state,
        mode: ComposerMode.Start,
        existingClipbooks: action.index,
      };

    case ActionType.START_LOADING_CLIPBOOK:
      return { ...state, mode: ComposerMode.LoadingClipbook };

    case ActionType.ABORT_LOADING_CLIPBOOK:
      return { ...state, mode: ComposerMode.Start };

    case ActionType.FINISH_LOADING_CLIPBOOK: {
      const intermediateState = {
        ...state,
        mode: (state.clipboard === null && action.clipbook.pages.length > 0) ?
          ComposerMode.Editing : ComposerMode.NewPage,
        clipbook: action.clipbook,
        pagePictures: action.pagePictures,
        drawings: action.drawings,
        drawingPictures: action.drawingPictures,
        history: initHistory(),
      };
      return resetView(intermediateState);
    }

    case ActionType.FINISH_SAVING_CLIPBOOK: {
      return {
        ...state,

        // Item which is saved on the disk but no longer used may exist.
        pagePictures: prunePictureItems(
          state.pagePictures,
          createIsPagePictureUsed(state.clipbook, state.clipboard, state.history),
        ),
        drawingPictures: prunePictureItems(
          state.drawingPictures,
          createIsDrawingPictureUsed(state.drawings, state.history),
        ),

        autoSaving: (
          isAutoSavingRunning(state.autoSaving)
            ? autoSaving(state.autoSaving, AutoSavingAction.finishProcess())
            : state.autoSaving
        ),
        savingClipbook: null,
      };
    }
  }
}

function doReduceEntireComposerStateByAction(
  state: EntireComposerState,
  action: Action,
): EntireComposerState {
  if (DrawingToolAction.isDomesticAction(action)) {
    const subState = state.drawingTool;
    const nextSubState = drawingTool(subState, action);
    return (subState === nextSubState) ? state : { ...state, drawingTool: nextSubState };
  }

  if (HistoryAction.isDomesticAction(action)) {
    const snapshot = recordForAction(state.history, action);
    const currentData = dataReplacedBySnapshot(state, snapshot);
    let intermediateState = consumeSnapshot(state, snapshot);
    intermediateState = applyClipbookModification(intermediateState);
    return {
      ...intermediateState,
      history: history(intermediateState.history, currentData, action),
    };
  }

  if (isDrawingAction(action)) {
    const { clipbook } = state;
    if (clipbook === null) {
      throw new Error('Illegal state.');
    }

    const { currentPage } = clipbook;
    if (currentPage === null) {
      throw new Error('Illegal state.');
    }

    let intermediateState = state;
    if (isEditingDrawingAction(action)) {
      intermediateState = takeDrawingSnapshot(intermediateState);
    }

    const { uniqueID } = clipbook.pages[currentPage];
    const subState = intermediateState.drawings[uniqueID];
    const nextSubState = drawing(subState, action);
    if (subState === nextSubState) {
      return intermediateState;
    }

    const drawings = { ...intermediateState.drawings };
    drawings[uniqueID] = nextSubState;
    intermediateState = { ...intermediateState, drawings };

    const nextDrawingPictures = (
      reducePictureStorageByDrawingAction(intermediateState.drawingPictures, action)
    );

    if (intermediateState.drawingPictures !== nextDrawingPictures) {
      intermediateState = {
        ...intermediateState,
        drawingPictures: nextDrawingPictures,
      };
    }

    return applyClipbookModification(intermediateState);
  }

  if (PictureStorageAction.isDomesticAction<PagePicture>(action, ComposerPictureStorage.Pages)) {
    const nextPagePictures = pictureStorage(state.pagePictures, action);
    if (state.pagePictures === nextPagePictures) {
      return state;
    }
    return { ...state, pagePictures: nextPagePictures };
  }

  if (
    PictureStorageAction.isDomesticAction<HTMLCanvasElement>(action, ComposerPictureStorage.Drawing)
  ) {
    const nextDrawingPictures = pictureStorage(state.drawingPictures, action);
    if (state.drawingPictures === nextDrawingPictures) {
      return state;
    }
    return { ...state, drawingPictures: nextDrawingPictures };
  }

  if (isComposerAction(action)) {
    let intermediateState = state;
    switch (action.type) {
      case ActionType.ADD_NEW_PAGE:
      case ActionType.ADD_NEW_PAGE_WITH_PICTURE: {
        const { clipbook } = state;
        if (clipbook === null) {
          throw new Error('Illegal state.');
        }

        const { currentPage } = clipbook;
        if (currentPage !== null) {
          // The first addition is not to be undone.
          intermediateState = takeClipbookSnapshot(intermediateState);
        }

        break;
      }
    }

    return reduceEntireComposerStateNew(intermediateState, action);
  }

  if (AutoSavingAction.isDomesticAction(action)) {
    const nextAutoSaving = autoSaving(state.autoSaving, action);
    return state.autoSaving === nextAutoSaving ? state : { ...state, autoSaving: nextAutoSaving };
  }

  return state;
}

export function composer(
  state: EntireComposerState = initEntireComposerState(),
  action: Action,
): EntireComposerState {
  let intermediateState = doReduceEntireComposerStateByAction(state, action);

  let nextPagePictures = intermediateState.pagePictures;
  let nextDrawingPictures = intermediateState.drawingPictures;

  const isViewMoving = (
    state.clipbook !== intermediateState.clipbook
    || (
      state.clipbook !== null
      && state.clipbook.currentPage !== intermediateState.clipbook!.currentPage
    )
  );

  if (isViewMoving) {
    nextPagePictures = preparePictureItemsForDrawing(
      nextPagePictures,
      createIsPageImageDemanded(intermediateState.clipbook),
    );
  }

  if (isViewMoving || state.drawings !== intermediateState.drawings) {
    nextDrawingPictures = preparePictureItemsForDrawing(
      nextDrawingPictures,
      createIsDrawingImageDemanded(intermediateState.clipbook, intermediateState.drawings),
    );
  }

  if (
    state.clipbook !== intermediateState.clipbook
    || state.clipboard !== intermediateState.clipboard
    || state.history !== intermediateState.history
  ) {
    nextPagePictures = prunePictureItems(
      nextPagePictures,
      createIsPagePictureUsed(
        intermediateState.clipbook,
        intermediateState.clipboard,
        intermediateState.history,
      ),
    );
  }

  if (
    state.drawings !== intermediateState.drawings
    || state.history !== intermediateState.history
  ) {
    nextDrawingPictures = prunePictureItems(
      nextDrawingPictures,
      createIsDrawingPictureUsed(intermediateState.drawings, intermediateState.history),
    );
  }

  if (
    intermediateState.pagePictures !== nextPagePictures
    || intermediateState.drawingPictures !== nextDrawingPictures
  ) {
    intermediateState = {
      ...intermediateState,
      pagePictures: nextPagePictures,
      drawingPictures: nextDrawingPictures,
    };
  }

  if (
    didAutoSavingStart(state.autoSaving, intermediateState.autoSaving)
    || didAutoSavingRestart(state.autoSaving, intermediateState.autoSaving)
  ) {
    intermediateState = prepareClipbookSaving(intermediateState);
  }

  if (
    isReadyToSaveClipbook(
      intermediateState.savingClipbook,
      intermediateState.pagePictures,
      intermediateState.drawingPictures,
    )
    && (
      state.savingClipbook !== intermediateState.savingClipbook
      || (
        !isReadyToSaveClipbook(state.savingClipbook, state.pagePictures, state.drawingPictures)
      )
    )
  ) {
    intermediateState = startClipbookSaving(intermediateState);
  }

  return intermediateState;
}
